import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import {
  useEnvoyerConvocation,
  useIntegration,
  usePreparerIntegration,
  type LigneIntegration,
} from '../lib/useIntegration';
import { ErreurApi } from '../lib/api';

/**
 * Écran « Intégration & convocations » du pôle pédagogique (CDC §6.2).
 *
 * Les entrées en formation à venir, avec leur décompte en jours civils
 * réunionnais, et la convocation pré-remplie depuis le dossier. Une convocation
 * incomplète est refusée : l'écran nomme ce qui manque plutôt que d'envoyer un
 * document à trous.
 */
export function PedIntegrationPage(): JSX.Element {
  const { site } = usePerimetre();
  const [imminentes, setImminentes] = useState(false);
  const [ouverte, setOuverte] = useState<string | null>(null);

  const { data, isLoading, error } = useIntegration(site, imminentes);
  const preparer = usePreparerIntegration();
  const convoquer = useEnvoyerConvocation();

  const compteurs = data?.compteurs;
  const entrees = data?.entrees ?? [];
  const active = entrees.find((entree) => entree.apprenantId === ouverte) ?? entrees[0] ?? null;
  const enCours = preparer.isPending || convoquer.isPending;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Intégration & convocations"
        sousTitre={`Pôle pédagogique · alerte nouveau contrat, intégration sous ${compteurs?.preavisJours ?? 8} jours, convocation pré-remplie`}
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Nouveaux contrats (7 j)"
          valeur={compteurs?.nouveauxContrats ?? '—'}
          detail="alerte au pôle pédagogique"
          ton="navy"
        />
        <Compteur
          libelle={`Intégrations sous ${compteurs?.preavisJours ?? 8} j`}
          valeur={compteurs?.imminentes ?? '—'}
          detail="convocation à envoyer"
          ton="warm"
        />
        <Compteur
          libelle="Convocations envoyées"
          valeur={compteurs?.convocationsEnvoyees ?? '—'}
          detail="depuis le début du mois"
          ton="mint"
        />
        <Compteur
          libelle="Sans formateur référent"
          valeur={compteurs?.sansFormateurReferent ?? '—'}
          detail="à affecter avant l’entrée"
          ton="blue"
        />
      </div>

      {convoquer.data && (
        <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          {convoquer.data.message} {convoquer.data.destinataires} destinataire(s) prévenu(s).
        </div>
      )}

      {(preparer.error instanceof ErreurApi || convoquer.error instanceof ErreurApi) && (
        <MessageErreur
          message={
            (preparer.error instanceof ErreurApi && preparer.error.message) ||
            (convoquer.error instanceof ErreurApi && convoquer.error.message) ||
            'Action refusée.'
          }
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          <Carte
            titre="Entrées en formation à venir"
            actions={
              <button
                type="button"
                onClick={() => setImminentes((valeur) => !valeur)}
                aria-pressed={imminentes}
                className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition ${
                  imminentes
                    ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                    : 'border-gray-200 bg-gray-50 text-codialis-gray'
                }`}
              >
                Sous {compteurs?.preavisJours ?? 8} jours
              </button>
            }
            sansBordure
          >
            {error && (
              <div className="p-4 sm:p-6">
                <MessageErreur
                  message={
                    error instanceof ErreurApi
                      ? error.message
                      : 'Les entrées en formation n’ont pas pu être chargées.'
                  }
                />
              </div>
            )}
            {isLoading && <Chargement libelle="Chargement des entrées…" />}

            <div className="divide-y divide-gray-100">
              {!isLoading && !error && entrees.length === 0 && (
                <p className="px-4 sm:px-6 py-10 text-center text-sm text-codialis-gray">
                  Aucune entrée en formation à venir sur ce périmètre
                </p>
              )}
              {entrees.map((entree) => (
                <button
                  key={entree.apprenantId}
                  type="button"
                  onClick={() => setOuverte(entree.apprenantId)}
                  className={`flex w-full items-center gap-4 px-4 sm:px-6 py-4 text-left transition hover:bg-gray-50 ${
                    active?.apprenantId === entree.apprenantId ? 'bg-codialis-sand' : ''
                  }`}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
                    {entree.apprenant
                      .split(' ')
                      .map((mot) => mot[0])
                      .join('')
                      .slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-codialis-navy">
                      {entree.apprenant}
                      {entree.nouveauContrat && (
                        <span className="ml-2">
                          <Pastille texte="Nouveau contrat" ton="mint" />
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-codialis-gray">
                      {entree.formation ?? 'Formation à définir'} · {entree.siteCode} · entrée le{' '}
                      {entree.dateEntreeLisible}
                      {entree.formateurReferent ? ` · ${entree.formateurReferent}` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <Pastille
                      texte={`${entree.decompte} · ${entree.etatLibelle}`}
                      ton={entree.etatTon}
                    />
                    {entree.convocationEnvoyeeLe && (
                      <div className="mt-1">
                        <Pastille texte="Convoqué" ton="success" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </Carte>
        </div>

        <div className="space-y-6">
          <Carte titre="Convocation pré-remplie" sansBordure>
            {active === null ? (
              <p className="p-4 sm:p-6 text-sm text-codialis-gray">
                Aucune convocation à préparer sur ce périmètre.
              </p>
            ) : (
              <PanneauConvocation
                entree={active}
                formateurs={data?.formateurs ?? []}
                documents={data?.documentsAApporter ?? []}
                enCours={enCours}
                onPreparer={(valeurs) =>
                  preparer.mutate({ apprenantId: active.apprenantId, ...valeurs })
                }
                onConvoquer={() => convoquer.mutate(active.apprenantId)}
              />
            )}
          </Carte>

          <Carte titre="Ce que reçoit le formateur référent" sansBordure>
            <div className="space-y-2 p-4 sm:p-6 text-sm text-codialis-gray">
              <p>
                À l’envoi de la convocation, le formateur référent est prévenu avec les dates
                clés du dossier : entrée, fin prévue et examen quand elles sont connues.
              </p>
              <p className="text-[11px]">
                Les envois suivent les canaux raccordés, et sont tracés dans tous les cas.
                L’écran des relances indique lesquels sont opérationnels.
              </p>
            </div>
          </Carte>
        </div>
      </div>
    </div>
  );
}

const CLASSE_CHAMP = 'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm';

function PanneauConvocation({
  entree,
  formateurs,
  documents,
  enCours,
  onPreparer,
  onConvoquer,
}: {
  entree: LigneIntegration;
  formateurs: { id: string; nom: string }[];
  documents: string[];
  enCours: boolean;
  onPreparer: (valeurs: {
    salleIntegration?: string;
    heureIntegration?: string;
    formateurReferentId?: string | null;
  }) => void;
  onConvoquer: () => void;
}): JSX.Element {
  const [salle, setSalle] = useState(entree.salleIntegration ?? '');
  const [heure, setHeure] = useState(entree.heureIntegration ?? '08:30');
  const [formateur, setFormateur] = useState(entree.formateurReferentId ?? '');

  const modifie =
    salle !== (entree.salleIntegration ?? '') ||
    heure !== (entree.heureIntegration ?? '08:30') ||
    formateur !== (entree.formateurReferentId ?? '');

  return (
    <div className="space-y-3 p-4 sm:p-6 text-sm">
      <div className="space-y-1.5 rounded-xl bg-gray-50 p-4">
        <div className="font-display font-bold text-codialis-navy">
          Convocation d’entrée en formation
        </div>
        {[
          ['Apprenant', entree.apprenant],
          ['Formation', entree.formation ?? 'à définir'],
          ['Site', entree.siteCode],
          ['Date', entree.dateEntreeLisible],
          ['Entreprise', entree.entreprise ?? 'sans entreprise'],
        ].map(([libelle, valeur]) => (
          <div key={libelle} className="flex justify-between gap-3 text-xs">
            <span className="text-codialis-gray">{libelle}</span>
            <span className="text-right font-medium text-codialis-navy">{valeur}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="pedintegration-salle" className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-codialis-gray">
            Salle
          </label>
          <input id="pedintegration-salle"
            value={salle}
            onChange={(evenement) => setSalle(evenement.target.value)}
            placeholder="Salle B2"
            className={CLASSE_CHAMP}
          />
        </div>
        <div>
          <label htmlFor="pedintegration-heure" className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-codialis-gray">
            Heure
          </label>
          <input id="pedintegration-heure"
            type="time"
            value={heure}
            onChange={(evenement) => setHeure(evenement.target.value)}
            className={CLASSE_CHAMP}
          />
        </div>
      </div>

      <div>
        <label htmlFor="pedintegration-formateur-referent" className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-codialis-gray">
          Formateur référent
        </label>
        <select id="pedintegration-formateur-referent"
          value={formateur}
          onChange={(evenement) => setFormateur(evenement.target.value)}
          className={CLASSE_CHAMP}
        >
          <option value="">À affecter</option>
          {formateurs.map((entreeFormateur) => (
            <option key={entreeFormateur.id} value={entreeFormateur.id}>
              {entreeFormateur.nom}
            </option>
          ))}
        </select>
      </div>

      {modifie && (
        <button
          type="button"
          onClick={() =>
            onPreparer({
              ...(salle.trim() ? { salleIntegration: salle.trim() } : {}),
              ...(heure ? { heureIntegration: heure } : {}),
              formateurReferentId: formateur === '' ? null : formateur,
            })
          }
          disabled={enCours}
          className="w-full rounded-xl border border-gray-200 bg-white py-2 text-xs font-semibold text-codialis-navy disabled:opacity-50"
        >
          Enregistrer la préparation
        </button>
      )}

      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-codialis-gray">
          Documents à apporter
        </div>
        <ul className="list-inside list-disc text-xs text-codialis-gray">
          {documents.map((document) => (
            <li key={document}>{document}</li>
          ))}
        </ul>
      </div>

      {entree.manques.length > 0 ? (
        <div className="rounded-xl bg-amber-50 p-3 text-[11px] text-codialis-navy">
          Convocation non envoyable : il manque {entree.manques.join(', ')}. Un apprenant
          convoqué sans ces informations se présenterait au mauvais endroit.
        </div>
      ) : (
        <p className="text-[11px] text-codialis-gray">
          Tous les champs sont repris du dossier, sans aucune saisie manuelle.
        </p>
      )}

      <BoutonPrincipal onClick={onConvoquer} disabled={enCours || entree.manques.length > 0}>
        {enCours ? 'Envoi…' : 'Envoyer à l’apprenant + formateur'}
      </BoutonPrincipal>

      {entree.convocationDocumentId && (
        <Link
          to={`/adm-documents/${entree.apprenantId}`}
          className="block text-center text-xs font-semibold text-codialis-sky hover:underline"
        >
          Voir la convocation générée
        </Link>
      )}
    </div>
  );
}
